/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import { RichText } from '@/components/common/RichText';
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Stethoscope, Send, ShieldAlert, Loader2, Sparkles, RefreshCw, ClipboardList, Calendar } from 'lucide-react';
import { startSymptomSession, sendSymptomMessage, listSymptomSessions, getSymptomSession } from '@/lib/ai_clinical';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { StartForm } from '@/components/symptomChecker/StartForm';
import { AssistantCard } from '@/components/symptomChecker/AssistantCard';

const SymptomCheckerPage = () => {
  const [sessionId, setSessionId] = useState(null);
  const [assistant, setAssistant] = useState(null);
  const [messages, setMessages] = useState([]); // {role,text}
  const [input, setInput] = useState('');
  const [starting, setStarting] = useState(false);
  const [sending, setSending] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const loadSessions = async () => {
    try {
      const { sessions } = await listSymptomSessions();
      setSessions(sessions || []);
    } catch (e) {
      console.error('SymptomChecker: load sessions failed', e);
    } finally {
      setLoadingSessions(false);
    }
  };

  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, assistant, sending]);

  const start = async (payload) => {
    setStarting(true);
    try {
      const res = await startSymptomSession(payload);
      setSessionId(res.session_id);
      setMessages([{ role: 'user', text: payload.initial_complaint }]);
      setAssistant(res.assistant);
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile avviare la sessione');
    } finally {
      setStarting(false);
    }
  };

  const send = async () => {
    if (!input.trim() || !sessionId || sending) return;
    const msg = input.trim();
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setSending(true);
    try {
      const res = await sendSymptomMessage(sessionId, msg);
      setAssistant(res.assistant);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore invio messaggio');
    } finally {
      setSending(false);
    }
  };

  const openSession = async (sid) => {
    try {
      const { session } = await getSymptomSession(sid);
      setSessionId(sid);
      const msgs = (session.messages || []).map((m) => ({ role: m.role, text: m.text }));
      setMessages(msgs);
      // find latest assistant with json
      const last = [...(session.messages || [])].reverse().find((m) => m.role === 'assistant' && m.json);
      setAssistant(last?.json || null);
    } catch (e) {
      toast.error('Sessione non caricabile');
    }
  };

  const resetForNew = () => {
    setSessionId(null);
    setAssistant(null);
    setMessages([]);
    setInput('');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 lg:py-10" data-testid="symptom-checker-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-primary"><Sparkles className="h-5 w-5" /><span className="text-xs uppercase tracking-wider font-bold">Nexi AI Clinico</span></div>
          <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">Sintomi & Triage AI</h1>
          <p className="text-muted-foreground text-sm md:text-base mt-1 max-w-xl">Descrivi come ti senti. L’AI ti aiuterà a capire l’urgenza, ma non sostituisce mai il tuo medico.</p>
        </div>
        <Button variant="outline" onClick={resetForNew} data-testid="symptom-new-session-btn" className="hidden md:inline-flex"><RefreshCw className="h-4 w-4" /> Nuova sessione</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-5">
          {!sessionId ? (
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><Stethoscope className="h-5 w-5 text-primary" />Racconta i tuoi sintomi</CardTitle></CardHeader>
              <CardContent><StartForm onSubmit={start} loading={starting} /></CardContent>
            </Card>
          ) : (
            <>
              <div ref={scrollRef} className="space-y-3 max-h-[52vh] overflow-y-auto scrollbar-thin pr-1">
                <AnimatePresence initial={false}>
                  {messages.map((m, i) => (
                    <motion.div
                      key={`${m.role}-${i}`}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}
                    >
                      <div className={cn(
                        'rounded-2xl px-3.5 py-2.5 text-sm max-w-[85%]',
                        m.role === 'user' ? 'bg-primary text-primary-foreground whitespace-pre-wrap' : 'bg-muted text-foreground'
                      )}>
                        {m.role === 'user' ? m.text : <RichText text={m.text} />}
                      </div>
                    </motion.div>
                  ))}
                  {sending && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="symptom-thinking">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Nexi sta analizzando i tuoi sintomi…
                    </div>
                  )}
                </AnimatePresence>
              </div>

              <AssistantCard assistant={assistant} />

              <Card>
                <CardContent className="pt-4 space-y-2">
                  <Label htmlFor="reply" className="text-xs uppercase tracking-wider text-muted-foreground">Rispondi o aggiungi dettagli</Label>
                  <div className="flex gap-2">
                    <Textarea id="reply" value={input} onChange={(e) => setInput(e.target.value)} rows={2} placeholder="Aggiungi informazioni utili…" onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} data-testid="symptom-reply-input" />
                    <Button onClick={send} disabled={!input.trim() || sending} className="self-end" data-testid="symptom-reply-send"><Send className="h-4 w-4" /></Button>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={() => navigate('/telemedicine')} data-testid="symptom-book-visit-btn"><Calendar className="h-3.5 w-3.5" /> Prenota una visita</Button>
                    <Button variant="outline" size="sm" onClick={resetForNew} className="md:hidden"><RefreshCw className="h-3.5 w-3.5" /> Nuova sessione</Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        <aside className="space-y-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ClipboardList className="h-4 w-4" />Le tue sessioni</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto scrollbar-thin">
              {loadingSessions ? (
                <>
                  <Skeleton className="h-14" /><Skeleton className="h-14" />
                </>
              ) : sessions.length === 0 ? (
                <div className="text-xs text-muted-foreground">Nessuna sessione precedente.</div>
              ) : (
                sessions.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => openSession(s.id)}
                    className={cn(
                      'w-full text-left rounded-xl border p-3 hover:bg-accent/50 transition-colors',
                      sessionId === s.id ? 'border-primary/60 bg-primary/5' : 'border-border/60'
                    )}
                    data-testid={`symptom-session-item-${s.id}`}
                  >
                    <div className="text-sm font-medium line-clamp-2">{s.initial_complaint}</div>
                    <div className="text-[10px] text-muted-foreground mt-1">{new Date(s.updated_at).toLocaleString('it')}</div>
                  </button>
                ))
              )}
            </CardContent>
          </Card>

          <Alert>
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle className="text-xs">Ricorda</AlertTitle>
            <AlertDescription className="text-xs">L’AI fornisce solo indicazioni orientative. Non è una diagnosi.</AlertDescription>
          </Alert>
        </aside>
      </div>
    </div>
  );
};

export default SymptomCheckerPage;
